import React, { useState } from 'react';
import type { IconName } from '@goodfynd/ui';
import { ButtonLink } from '@goodfynd/ui';
import { Icon } from '@goodfynd/ui';
import { Text } from '@goodfynd/ui';

import Alert from './Alert';
import { StyledBody, StyledButton, StyledContent } from './styles';
import type { AlertProps, GeneralAlertProps } from './types';

const getIcon = (type: AlertProps['type']): IconName => {
  switch (type) {
    case 'error':
      return 'caution';
    case 'info':
      return 'info';
    case 'success':
      return 'check';
    case 'warning':
      return 'caution_alt';

    case undefined:
    default:
      return 'chevron_right';
  }
};

export default function GeneralAlert({
  onDismiss,
  onClicked,
  busy,
  css,
  title = '',
  message = '',
  link = undefined,
  linkText = 'Learn More',
  icon,
  type,
  children,
}: GeneralAlertProps) {
  const [isDismissing, setIsDismissing] = useState(false);

  const handleDismiss = async () => {
    if (isDismissing || !onDismiss) return;
    setIsDismissing(true);
    await onDismiss();
  };

  const handleClick = async () => {
    if (isDismissing) return;
    setIsDismissing(true);
    if (onClicked) {
      onClicked();
    }
  };

  // If is dismissing, return null instead of false
  if (isDismissing) {
    return null;
  }

  return (
    <Alert
      showIcon={false}
      type={type}
      css={{
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: 24,
        maxWidth: '100%',
        width: 1121,
        '@xs': {
          marginBottom: 16,
        },
        ...css,
      }}
    >
      <Icon
        name={icon || getIcon(type)}
        size={24}
        style={{
          alignSelf: 'center',
          color: 'inherit',
          marginRight: 8,
          display: busy ? 'none' : 'block',
          marginTop: 2,
          flexShrink: 0,
        }}
      />
      <StyledContent>
        <StyledBody>
          <Text as="subhead2">{title}</Text>
          <Text as="body">{message}</Text>
          {!busy && link != undefined && (
            <ButtonLink
              display="compact"
              href={link}
              onClick={handleClick}
              size="medium"
              style={{ marginTop: 2 }}
            >
              {linkText}
            </ButtonLink>
          )}
          {children}
        </StyledBody>
        {onDismiss && (
          <StyledButton
            aria-label="Close alert"
            className="dismiss-button"
            disabled={isDismissing}
            onClick={handleDismiss}
            rank="tertiary"
            size="small"
          >
            <Icon name="close" size={24} />
          </StyledButton>
        )}
      </StyledContent>
    </Alert>
  );
}
